import React, { useCallback, useEffect } from 'react';
import { ColorResult } from 'react-color';
import { useRouteParams } from '../../hooks/useRouteParams';
import {
  initThemeConfig,
  TemplateMetaConfigType,
  updateColor,
  updateConfig,
} from '../../redux/configuration/slice';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { trackMixEvent, TRACK_EVENTS } from '../../services/analytics';
import Button from '../../styled/Button/Button';
import Divider from '../../styled/Divider';
import Switch from '../../styled/Switch';
import boxTemplates from '../../templates/box';
import Select from '../Select';
import TemplateSelector from '../TemplateSelector';
import ColorPicker from './ColorPicker';
import { TemplateConfigWrapper } from './TemplateConfig.styles';

interface TemplateConfigProps {}

const TemplateConfig: React.FC<TemplateConfigProps> = () => {
  const { template, type } = useRouteParams();
  const dispatch = useAppDispatch();
  const storeConfiguration = useAppSelector(
    ({ configuration }) => configuration
  );
  const { global: globalConfiguration, template: templateConfiguration } =
    storeConfiguration;

  const currentTemplate = boxTemplates[type].find(
    ({ name }) => template === name
  );

  // const colorsArr = globalConfiguration.enableGlobalColors
  //   ? globalConfiguration.colors
  //   : templateConfiguration[type][template].colors;

  const colorsArr = templateConfiguration[type]?.[template]?.colors;

  const { externalLink, linkRel, schemaEnabled } = globalConfiguration.config;

  const handleMetaConfigChange = (
    value: boolean | string | undefined,
    configKey: keyof TemplateMetaConfigType
  ) => {
    // eslint-disable-next-line no-underscore-dangle
    trackMixEvent(TRACK_EVENTS.META_CONFIG_CHANGE_ + configKey);
    dispatch(updateConfig({ value, configKey }));
  };

  const handleChangeColorTheme = (colorValue: ColorResult, colorId: string) => {
    // if (globalConfiguration.enableGlobalColors)
    //   dispatch(updateColor({ colorId, colorValue: colorValue.hex }));
    // else
    // eslint-disable-next-line no-underscore-dangle
    trackMixEvent(TRACK_EVENTS.CHANGE_COLOR_ + colorId);
    dispatch(
      updateColor({
        colorId,
        colorValue: colorValue.hex,
        template,
        type,
      })
    );
  };

  const updateInitialColor = useCallback(() => {
    trackMixEvent(TRACK_EVENTS.RESET_COLOR);
    const currentTemplateColorConfig = currentTemplate?.config.colors || [];

    const defaultColorData = currentTemplateColorConfig.map(
      ({ colorLabel, colorValue }) => ({
        colorLabel,
        colorValue,
        colorId: colorLabel.toLowerCase().split(' ')[0],
      })
    );

    dispatch(initThemeConfig({ colors: defaultColorData, type, template }));
  }, [currentTemplate, dispatch, template, type]);

  useEffect(() => {
    if (!colorsArr) updateInitialColor();
  }, [colorsArr, updateInitialColor]);

  const relOptions = [
    { value: 'nofollow', label: 'nofollow' },
    { value: 'sponsored', label: 'sponsored' },
    { value: 'ugc', label: 'ugc' },
  ];

  return (
    <TemplateConfigWrapper>
      <Divider styles={{ marginTop: 0 }}>Template</Divider>
      <TemplateSelector />
      <Divider>Colors</Divider>
      {colorsArr?.map(({ colorId, colorValue, colorLabel }) => (
        <ColorPicker
          key={colorId}
          label={colorLabel}
          color={colorValue}
          onChange={(color) => handleChangeColorTheme(color, colorId)}
        />
      ))}
      <Button varient="link" onClick={updateInitialColor} className="reset">
        Reset Colors
      </Button>
      <Divider>Configure</Divider>

      {/* <div className="switch">
        <Switch
          varient="small"
          checked={schemaEnabled}
          onChange={(e) =>
            handleMetaConfigChange(e.target.checked, 'schemaEnabled')
          }
        />
        <p>Generate Schema</p>
      </div> */}
      <div className="switch">
        <Switch
          varient="small"
          checked={externalLink}
          onChange={(e) =>
            handleMetaConfigChange(e.target.checked, 'externalLink')
          }
        />
        <p>Open Links in New Window</p>
      </div>
      <p className="relOptionsLabel">Link Rel</p>
      <Select
        options={relOptions}
        value={{ value: linkRel, label: linkRel }}
        onChange={(e) => handleMetaConfigChange(e?.value, 'linkRel')}
        isSearchable={false}
        lane
        className="relOptions"
      />
    </TemplateConfigWrapper>
  );
};

export default TemplateConfig;
